import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BlogPost = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [notionSource, setNotionSource] = useState(false);

  // Static blog content - can be connected to Notion later
  const blogContent = {
    'ductless-vs-ducted-laminar-air-flow': {
      id: 1,
      title: 'Ductless vs Ducted Laminar Air Flow: Which is Better for Your Hospital?',
      category: 'Products',
      author: 'ALMED Team',
      date: '2025-01-15',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1200',
      readTime: '5 min read',
      content: `
        <h2>Understanding Laminar Air Flow Systems</h2>
        <p>Laminar air flow systems are critical components in modern healthcare facilities, designed to provide sterile air environments in operation theatres, ICUs, and other critical areas. The choice between ductless and ducted systems can significantly impact both installation costs and long-term operational efficiency.</p>
        
        <h2>What is a Ducted Laminar Air Flow System?</h2>
        <p>Traditional ducted systems require extensive HVAC ductwork throughout the facility. Air is processed in a central location and distributed through a network of ducts to various rooms. While effective, these systems come with several challenges:</p>
        <ul>
          <li>High installation costs due to ductwork requirements</li>
          <li>Complex maintenance procedures</li>
          <li>Potential for contamination within duct systems</li>
          <li>Significant space requirements for ductwork</li>
          <li>Longer installation timelines</li>
        </ul>
        
        <h2>ALMED's Patented Ductless Laminar Air Flow System</h2>
        <p>Our innovative ductless system eliminates the need for complex ductwork by creating a sterile air tent directly above the patient or surgical area. This patented technology offers numerous advantages:</p>
        <ul>
          <li><strong>Cost-Effective:</strong> 40-60% lower installation costs</li>
          <li><strong>Easy Installation:</strong> Can be installed in existing facilities without major construction</li>
          <li><strong>Low Maintenance:</strong> Simple filter replacement and cleaning procedures</li>
          <li><strong>Class I Air Quality:</strong> Achieves the highest air quality standards</li>
          <li><strong>Energy Efficient:</strong> Lower power consumption compared to ducted systems</li>
          <li><strong>Portable Options:</strong> Can be moved between rooms as needed</li>
        </ul>
        
        <h2>Technical Specifications Comparison</h2>
        <table>
          <thead>
            <tr>
              <th>Feature</th>
              <th>Ductless (ALMED)</th>
              <th>Ducted (Traditional)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Installation Time</td>
              <td>1-2 days</td>
              <td>2-4 weeks</td>
            </tr>
            <tr>
              <td>Air Quality Class</td>
              <td>Class I</td>
              <td>Class I</td>
            </tr>
            <tr>
              <td>Maintenance Frequency</td>
              <td>Quarterly</td>
              <td>Monthly</td>
            </tr>
            <tr>
              <td>Power Consumption</td>
              <td>Low</td>
              <td>High</td>
            </tr>
            <tr>
              <td>Space Requirement</td>
              <td>Minimal</td>
              <td>Significant</td>
            </tr>
          </tbody>
        </table>
        
        <h2>When to Choose Ductless Systems</h2>
        <p>Ductless laminar air flow systems are ideal for:</p>
        <ul>
          <li>Existing hospitals looking to upgrade without major renovation</li>
          <li>New facilities with budget constraints</li>
          <li>Facilities requiring quick installation</li>
          <li>Mobile or temporary healthcare setups</li>
          <li>Hospitals seeking energy-efficient solutions</li>
        </ul>
        
        <h2>Conclusion</h2>
        <p>While both ducted and ductless systems can achieve Class I air quality, ALMED's patented ductless laminar air flow system offers significant advantages in terms of cost, installation time, and maintenance. Our system has been trusted by over 200 hospitals and government projects across India.</p>
        
        <p>Contact ALMED Equipment today to learn more about how our ductless laminar air flow system can benefit your healthcare facility.</p>
      `
    },
    'modular-ot-complete-guide': {
      id: 2,
      title: 'Complete Guide to Modular Operation Theatres',
      category: 'Products',
      author: 'ALMED Team',
      date: '2025-01-10',
      image: 'https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=1200',
      readTime: '8 min read',
      content: `
        <h2>What is a Modular Operation Theatre?</h2>
        <p>A Modular Operation Theatre (OT) is a prefabricated, factory-built surgical suite that offers superior infection control, better air quality, and easier maintenance compared to traditional brick-and-mortar operation theatres. These units are designed to meet international standards for surgical environments.</p>
        
        <h2>Key Components of a Modular OT</h2>
        <h3>1. Wall Panels</h3>
        <p>Made from SS304 stainless steel or coated GI sheets, these panels are seamless, easy to clean, and resistant to bacterial growth. ALMED uses pharmaceutical-grade materials that ensure durability and hygiene.</p>
        
        <h3>2. Flooring</h3>
        <p>Epoxy flooring or seamless vinyl flooring provides a continuous, joint-free surface that prevents bacterial accumulation. Our flooring solutions are chemical-resistant and easy to maintain.</p>
        
        <h3>3. Ceiling System</h3>
        <p>Flush-mounted ceiling panels with integrated lighting and laminar air flow systems ensure a clean, efficient surgical environment.</p>
        
        <h3>4. Air Handling Unit (AHU)</h3>
        <p>Critical for maintaining positive pressure and air quality. ALMED's ductless systems provide Class I air quality without complex ductwork.</p>
        
        <h3>5. Surgical Pendants</h3>
        <p>Ceiling-mounted pendants provide gas supply, electrical connections, and equipment mounting points within easy reach of the surgical team.</p>
        
        <h2>Benefits of Modular OT</h2>
        <ul>
          <li><strong>Infection Control:</strong> Seamless surfaces and positive pressure prevent contamination</li>
          <li><strong>Quick Installation:</strong> 70% faster than conventional OT construction</li>
          <li><strong>Cost Effective:</strong> Lower construction and maintenance costs</li>
          <li><strong>Flexibility:</strong> Can be relocated or expanded as needed</li>
          <li><strong>Compliance:</strong> Meets NABH and international standards</li>
        </ul>
        
        <h2>ALMED's Modular OT Solutions</h2>
        <p>We offer complete turnkey solutions including design, fabrication, installation, and after-sales support. Our modular OTs are trusted by leading hospitals and government healthcare projects across India.</p>
        
        <h2>Installation Process</h2>
        <ol>
          <li>Site survey and assessment</li>
          <li>Custom design based on requirements</li>
          <li>Factory fabrication of panels</li>
          <li>On-site installation (typically 2-3 weeks)</li>
          <li>Testing and commissioning</li>
          <li>Staff training and handover</li>
        </ol>
        
        <p>Contact us for a free consultation and quote for your modular OT project.</p>
      `
    },
    'psa-oxygen-plant-benefits': {
      id: 3,
      title: 'Why Every Hospital Needs a PSA Oxygen Generation Plant',
      category: 'Products',
      author: 'ALMED Team',
      date: '2025-01-05',
      image: 'https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?w=1200',
      readTime: '6 min read',
      content: `
        <h2>The Critical Role of Medical Oxygen</h2>
        <p>Medical oxygen is essential for patient care, from emergency rooms to ICUs. The COVID-19 pandemic highlighted the vulnerability of hospitals dependent on cylinder-based oxygen supply. PSA (Pressure Swing Adsorption) oxygen generation plants offer a reliable, cost-effective alternative.</p>
        
        <h2>How PSA Technology Works</h2>
        <p>PSA plants use zeolite molecular sieves to separate oxygen from atmospheric air. The process involves:</p>
        <ol>
          <li>Air compression and filtration</li>
          <li>Adsorption of nitrogen by zeolite beds</li>
          <li>Collection of 93-95% pure oxygen</li>
          <li>Regeneration of zeolite beds</li>
        </ol>
        
        <h2>Benefits of On-Site Oxygen Generation</h2>
        <ul>
          <li><strong>Supply Security:</strong> Never run out of oxygen</li>
          <li><strong>Cost Savings:</strong> 50-70% lower costs vs. cylinder supply</li>
          <li><strong>Consistent Purity:</strong> 93-95% medical grade oxygen</li>
          <li><strong>24/7 Availability:</strong> Continuous production</li>
          <li><strong>Space Efficient:</strong> No cylinder storage needed</li>
          <li><strong>Environmental:</strong> Reduced transportation footprint</li>
        </ul>
        
        <h2>ALMED PSA Oxygen Plants</h2>
        <p>We offer PSA plants ranging from 5 Nm³/hr to 100 Nm³/hr capacity, suitable for small clinics to large hospitals. Our plants feature:</p>
        <ul>
          <li>Fully automatic operation</li>
          <li>Remote monitoring capability</li>
          <li>Low maintenance requirements</li>
          <li>Compact footprint</li>
          <li>Quick installation</li>
        </ul>
        
        <p>Contact ALMED for a site assessment and customized solution for your oxygen requirements.</p>
      `
    },
    'infection-control-healthcare': {
      id: 4,
      title: 'Infection Control in Healthcare: Best Practices for 2025',
      category: 'Healthcare',
      author: 'ALMED Team',
      date: '2024-12-28',
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1200',
      readTime: '7 min read',
      content: `
        <h2>The Importance of Infection Control</h2>
        <p>Hospital-acquired infections (HAIs) affect millions of patients worldwide each year. Effective infection control measures are crucial for patient safety and healthcare quality. This guide covers the latest best practices for 2025.</p>
        
        <h2>Key Areas of Focus</h2>
        <h3>1. Air Quality Management</h3>
        <p>Proper ventilation and air filtration are fundamental to infection control. HEPA filtration, positive pressure in critical areas, and laminar air flow systems help maintain sterile environments.</p>
        
        <h3>2. Surface Disinfection</h3>
        <p>Regular cleaning and disinfection of surfaces using appropriate agents. Consider UV-C and ozone-based disinfection for comprehensive coverage.</p>
        
        <h3>3. Hand Hygiene</h3>
        <p>Still the most effective infection prevention measure. Ensure adequate hand washing stations and sanitizer dispensers throughout the facility.</p>
        
        <h3>4. Isolation Protocols</h3>
        <p>Proper isolation rooms with negative pressure for infectious patients prevent airborne transmission.</p>
        
        <h2>Technology Solutions</h2>
        <p>Modern infection control incorporates technology such as:</p>
        <ul>
          <li>Automated air quality monitoring</li>
          <li>UV-C disinfection robots</li>
          <li>Ozone generators for room disinfection</li>
          <li>Smart HVAC systems</li>
        </ul>
        
        <p>ALMED Equipment offers comprehensive infection control solutions including laminar air flow systems, ozone generators, and modular isolation rooms.</p>
      `
    },
    'almed-mt-healthcare-award-2018': {
      id: 5,
      title: 'ALMED Wins MT India Healthcare Award 2018',
      category: 'Achievements',
      author: 'ALMED Team',
      date: '2024-12-20',
      image: 'https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?w=1200',
      readTime: '3 min read',
      content: `
        <h2>Recognition for Innovation</h2>
        <p>We are proud to announce that ALMED Equipment has been honored with the prestigious MT India Healthcare Award 2018 for "Innovative Solutions for Infection Control."</p>
        
        <h2>About the Award</h2>
        <p>The 8th MT India Healthcare Awards recognize excellence and innovation in the healthcare industry. Our patented Ductless Laminar Air Flow System was recognized for its contribution to improving infection control in healthcare facilities.</p>
        
        <h2>Our Journey</h2>
        <p>This award reflects our 15+ years of dedication to developing innovative healthcare equipment. From a small workshop to serving 200+ hospitals across India, we have consistently focused on quality and innovation.</p>
        
        <h2>Thank You</h2>
        <p>We thank our customers, partners, and team members who have been part of this journey. This recognition motivates us to continue developing solutions that improve patient safety and healthcare outcomes.</p>
      `
    },
    'ozone-generator-disinfection': {
      id: 6,
      title: 'Ozone Generators: Chemical-Free Disinfection for Modern Hospitals',
      category: 'Products',
      author: 'ALMED Team',
      date: '2024-12-15',
      image: 'https://images.unsplash.com/photo-1581093458791-9d42cc030552?w=1200',
      readTime: '5 min read',
      content: `
        <h2>What is Ozone Disinfection?</h2>
        <p>Ozone (O3) is a powerful oxidizing agent that effectively destroys bacteria, viruses, fungi, and other pathogens. Unlike chemical disinfectants, ozone leaves no harmful residues and converts back to oxygen after use.</p>
        
        <h2>Benefits of Ozone Disinfection</h2>
        <ul>
          <li><strong>Chemical-Free:</strong> No harmful residues or chemicals</li>
          <li><strong>Comprehensive:</strong> Reaches all surfaces and air</li>
          <li><strong>Fast-Acting:</strong> Quick disinfection cycles</li>
          <li><strong>Cost-Effective:</strong> No consumables needed</li>
          <li><strong>Environmentally Safe:</strong> Converts back to oxygen</li>
        </ul>
        
        <h2>Applications</h2>
        <p>ALMED's O3 GEN ozone generators are ideal for:</p>
        <ul>
          <li>Operation theatre disinfection</li>
          <li>ICU and patient room sanitization</li>
          <li>Laboratory decontamination</li>
          <li>CSSD areas</li>
          <li>Ambulance disinfection</li>
        </ul>
        
        <p>Contact us to learn more about our ozone disinfection solutions.</p>
      `
    },
    'iso-certification-importance': {
      id: 7,
      title: 'Why ISO Certification Matters in Healthcare Equipment',
      category: 'Company',
      author: 'ALMED Team',
      date: '2024-12-10',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200',
      readTime: '4 min read',
      content: `
        <h2>Understanding ISO Certification</h2>
        <p>ISO (International Organization for Standardization) certification demonstrates that a company meets international standards for quality management systems. For healthcare equipment manufacturers, this certification is crucial.</p>
        
        <h2>ALMED's ISO 9001:2008 Certification</h2>
        <p>ALMED Equipment is proud to be an ISO 9001:2008 certified company. This certification ensures:</p>
        <ul>
          <li>Consistent product quality</li>
          <li>Documented processes and procedures</li>
          <li>Continuous improvement culture</li>
          <li>Customer focus</li>
          <li>Risk management</li>
        </ul>
        
        <h2>What This Means for Our Customers</h2>
        <p>When you choose ALMED, you're choosing a company that:</p>
        <ul>
          <li>Adheres to international quality standards</li>
          <li>Has documented manufacturing processes</li>
          <li>Continuously improves products and services</li>
          <li>Maintains traceability and accountability</li>
        </ul>
        
        <p>Our certifications reflect our commitment to delivering reliable, high-quality healthcare equipment.</p>
      `
    },
    'government-hospital-projects': {
      id: 8,
      title: 'ALMED\'s Government Hospital Projects: A Success Story',
      category: 'Achievements',
      author: 'ALMED Team',
      date: '2024-12-05',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=1200',
      readTime: '6 min read',
      content: `
        <h2>Serving Public Healthcare</h2>
        <p>ALMED Equipment has been a trusted partner for government healthcare projects across India. Our equipment has been installed in numerous district hospitals, medical colleges, and public health centers.</p>
        
        <h2>Key Government Projects</h2>
        <p>We have successfully delivered:</p>
        <ul>
          <li>Modular Operation Theatres</li>
          <li>PSA Oxygen Generation Plants</li>
          <li>Laminar Air Flow Systems</li>
          <li>Isolation Ward Equipment</li>
        </ul>
        
        <h2>Why Government Trusts ALMED</h2>
        <ul>
          <li><strong>Quality:</strong> ISO certified manufacturing</li>
          <li><strong>Reliability:</strong> 15+ years of proven track record</li>
          <li><strong>Support:</strong> 24/7 service across India</li>
          <li><strong>Value:</strong> Cost-effective solutions</li>
          <li><strong>Compliance:</strong> Meets all government specifications</li>
        </ul>
        
        <h2>Impact</h2>
        <p>Our equipment serves millions of patients through government hospitals, contributing to improved healthcare outcomes across the nation.</p>
        
        <p>Partner with ALMED for your government healthcare project requirements.</p>
      `
    }
  };

  useEffect(() => {
    const fetchBlogPost = async () => {
      setLoading(true);
      
      try {
        // Try to fetch from API
        const response = await axios.get(`/api/blog/${slug}`);
        
        if (response.data.success && response.data.data) {
          setBlog(response.data.data);
          setNotionSource(response.data.source === 'notion');
          
          // Fetch related posts
          const relatedResponse = await axios.get(`/api/blog?category=${response.data.data.category}`);
          if (relatedResponse.data.success) {
            const related = relatedResponse.data.data
              .filter(b => b.slug !== slug)
              .slice(0, 3);
            setRelatedPosts(related);
          }
        } else {
          // Fallback to static content
          const content = blogContent[slug];
          if (content) {
            setBlog(content);
            const related = Object.values(blogContent)
              .filter(b => b.category === content.category && b.id !== content.id)
              .slice(0, 3);
            setRelatedPosts(related);
          }
        }
      } catch (error) {
        console.error('Error fetching blog post:', error);
        // Fallback to static content
        const content = blogContent[slug];
        if (content) {
          setBlog(content);
          const related = Object.values(blogContent)
            .filter(b => b.category === content.category && b.id !== content.id)
            .slice(0, 3);
          setRelatedPosts(related);
        }
      } finally {
        setLoading(false);
        // Scroll to top
        window.scrollTo(0, 0);
      }
    };

    fetchBlogPost();
  }, [slug]);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  if (loading) {
    return (
      <div className="loading" style={{minHeight: '100vh', marginTop: '70px'}}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="blog-not-found" style={{marginTop: '70px', padding: '4rem 2rem', textAlign: 'center'}}>
        <h1>Blog Post Not Found</h1>
        <p>The article you're looking for doesn't exist.</p>
        <Link to="/blog" className="btn btn-primary">Back to Blog</Link>
      </div>
    );
  }

  return (
    <div className="blog-post-page" style={{marginTop: '70px'}}>
      {/* Hero Image */}
      <div 
        className="blog-post-hero"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%), url(${blog.image})`
        }}
      >
        <div className="container">
          <Link to="/blog" className="blog-back-link">← Back to Blog</Link>
          <span className="blog-post-category">{blog.category}</span>
          <h1 className="blog-post-title">{blog.title}</h1>
          <div className="blog-post-meta">
            <span className="blog-post-author">by {blog.author}</span>
            <span className="blog-post-date">{formatDate(blog.date)}</span>
            <span className="blog-post-read-time">{blog.readTime}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="blog-post-content">
        <div className="container">
          <div 
            className="blog-post-body"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />

          {/* Share Section */}
          <div className="blog-post-share">
            <span>Share this article:</span>
            <div className="share-buttons">
              <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}&title=${encodeURIComponent(blog.title)}`} target="_blank" rel="noopener noreferrer" className="share-btn linkedin">
                LinkedIn
              </a>
              <a href={`https://twitter.com/intent/tweet?url=${window.location.href}&text=${encodeURIComponent(blog.title)}`} target="_blank" rel="noopener noreferrer" className="share-btn twitter">
                Twitter
              </a>
              <a href={`https://wa.me/?text=${encodeURIComponent(blog.title + ' ' + window.location.href)}`} target="_blank" rel="noopener noreferrer" className="share-btn whatsapp">
                WhatsApp
              </a>
            </div>
          </div>

          {/* CTA */}
          <div className="blog-post-cta">
            <h3>Interested in our solutions?</h3>
            <p>Contact us for a free consultation and quote.</p>
            <Link to="/contact" className="btn btn-primary">Get Quote</Link>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="blog-related section">
          <div className="container">
            <h2 className="section-title">Related Articles</h2>
            <div className="blog-related-grid">
              {relatedPosts.map((post) => (
                <Link key={post.id} to={`/blog/${Object.keys(blogContent).find(key => blogContent[key].id === post.id)}`} className="blog-card">
                  <div className="blog-card-image">
                    <img src={post.image} alt={post.title} />
                    <span className="blog-card-category">{post.category}</span>
                  </div>
                  <div className="blog-card-content">
                    <h3 className="blog-card-title">{post.title}</h3>
                    <div className="blog-card-footer">
                      <span className="blog-card-date">{formatDate(post.date)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;

